import { z } from 'zod';

/**
 * Canonical observation types ingested in Phase B. The key encodes the unit
 * (`_c` Celsius, `_ms` metres per second, `_pct` percent, `_deg` degrees) so
 * ingestion, API, and UI all agree on a single normalized form.
 */
export const OBSERVATION_TYPES = {
  air_temp_c: { unit: '°C', label: 'Air temperature' },
  rel_hum_pct: { unit: '%', label: 'Relative humidity' },
  wind_speed_ms: { unit: 'm/s', label: 'Wind speed' },
  wind_dir_deg: { unit: '°', label: 'Wind direction' },
  dewpoint_c: { unit: '°C', label: 'Dew point' },
  // PurpleAir community PM2.5 (E.1.11). The ingest worker writes observation_type
  // 'pm2.5_atm' (atmospheric correction); it MUST be a known key or the sensors
  // API's z.record(ObservationTypeKeySchema, …) rejects the whole feature with a
  // ZodError (Task #67). µg/m³, unbounded — the SidePanel colour-codes by AQI.
  'pm2.5_atm': { unit: 'µg/m³', label: 'PM2.5 (atmospheric)' },
} as const;

export type ObservationTypeKey = keyof typeof OBSERVATION_TYPES;

export const ObservationTypeKeySchema = z.enum(
  Object.keys(OBSERVATION_TYPES) as [ObservationTypeKey, ...ObservationTypeKey[]],
);

export const ObservationValueSchema = z.object({
  value: z.number(),
  observed_at: z.string().datetime({ offset: true }),
  unit: z.string(),
});
export type ObservationValue = z.infer<typeof ObservationValueSchema>;

export const SensorFeaturePropertiesSchema = z.object({
  name: z.string(),
  source: z.string(),
  external_id: z.string(),
  type: z.string(),
  latest_observations: z.record(ObservationTypeKeySchema, ObservationValueSchema),
});
export type SensorFeatureProperties = z.infer<typeof SensorFeaturePropertiesSchema>;

export const SensorFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: z.object({
    type: z.literal('Point'),
    coordinates: z.tuple([z.number(), z.number()]),
  }),
  properties: SensorFeaturePropertiesSchema,
});
export type SensorFeature = z.infer<typeof SensorFeatureSchema>;

export const SensorFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(SensorFeatureSchema),
});
export type SensorFeatureCollection = z.infer<typeof SensorFeatureCollectionSchema>;

export const SensorDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: z.object({
    type: z.literal('Point'),
    coordinates: z.tuple([z.number(), z.number()]),
  }),
  properties: z.object({
    name: z.string(),
    source: z.string(),
    external_id: z.string(),
    type: z.string(),
    metadata: z.record(z.unknown()).nullable(),
    observations_24h: z.record(
      ObservationTypeKeySchema,
      z.array(ObservationValueSchema),
    ),
  }),
});
export type SensorDetail = z.infer<typeof SensorDetailSchema>;
